import { looksLikeBackendUuid } from '../data/patientData'
import {
  createEligibilityCheck,
  type CreateEligibilityCheckPayload,
  type EligibilityIntegrationProvider,
} from './eligibilityChecksService'
import { STEDI_API_DATE } from './stediService'

export const ELIGIBILITY_PERSISTENCE_META_KEY = '_eligibilityPersistence'

export type EligibilityPersistenceMeta = {
  integrationProvider: EligibilityIntegrationProvider
  stediApiDate?: string
  requestPayload: Record<string, unknown>
  responsePayload: Record<string, unknown>
}

/** Optional overrides when saving POST /eligibility-checks. */
export type EligibilityCheckPersistContext = {
  /** Insurance member ID (form subscriberID). Sent as `member_id`. */
  memberId?: string
  /** Backend patients.id UUID only. Never a subscriber / member ID. */
  patientId?: string
  /** Persist a member row so dependent checks create a new patient. Default true. */
  savePatient?: boolean
  payorId?: string
  patientIntakeId?: string
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function getStediEligibilityResponse(
  meta: EligibilityPersistenceMeta
): Record<string, unknown> | null {
  return asRecord(meta.responsePayload.response) ?? asRecord(meta.responsePayload)
}

function getStediSubscriber(meta: EligibilityPersistenceMeta): Record<string, unknown> | null {
  return asRecord(getStediEligibilityResponse(meta)?.subscriber)
}

/** Selected patient insurance ID from the form, then the request envelope `memberId`. */
export function resolveMemberIdForEligibilityCheck(
  formData: { memberID?: string; subscriberID?: string; policyNumber?: string },
  parsed?: Record<string, unknown>
): string {
  const fromSelected = (formData.memberID || '').replace(/\s+/g, '').trim()
  if (fromSelected) return fromSelected

  const meta = parsed ? readEligibilityPersistenceMeta(parsed) : null
  const envelope = meta ? asRecord(meta.requestPayload) : null
  const fromEnvelope = String(envelope?.memberId ?? envelope?.member_id ?? '')
    .replace(/\s+/g, '')
    .trim()
  if (fromEnvelope) return fromEnvelope

  return (formData.subscriberID || formData.policyNumber || '')
    .replace(/\s+/g, '')
    .trim()
}

function getPverifyGroupNumber(parsed: Record<string, unknown>): string {
  const meta = readEligibilityPersistenceMeta(parsed)
  const response = asRecord(meta?.responsePayload) ?? parsed
  const plan = asRecord(response.PlanCoverageSummary) ?? asRecord(response.planCoverageSummary)
  const fromPlan = String(plan?.GroupNumber ?? plan?.groupNumber ?? '').trim()
  if (fromPlan) return fromPlan

  const demographic = asRecord(response.DemographicInfo) ?? asRecord(response.demographicInfo)
  const subscriber = asRecord(demographic?.Subscriber) ?? asRecord(demographic?.subscriber)
  return String(subscriber?.GroupNumber ?? subscriber?.groupNumber ?? '').trim()
}

/**
 * Stedi `patient_intake_id` = `response.subscriber.groupNumber` (then `planNumber`).
 * pVerify `patient_intake_id` = `PlanCoverageSummary.GroupNumber`, then form group/policy.
 */
export function resolvePatientIntakeIdForEligibilityCheck(
  parsed: Record<string, unknown>,
  formData?: { groupNumber?: string; policyNumber?: string }
): string {
  const meta = readEligibilityPersistenceMeta(parsed)
  if (!meta) return String(formData?.groupNumber ?? formData?.policyNumber ?? '').trim()

  if (meta.integrationProvider === 'stedi') {
    const subscriber = getStediSubscriber(meta)
    const groupNumber = String(subscriber?.groupNumber ?? '').trim()
    if (groupNumber) return groupNumber
    const planNumber = String(subscriber?.planNumber ?? '').trim()
    if (planNumber) return planNumber
  }

  if (meta.integrationProvider === 'pverify') {
    const fromResponse = getPverifyGroupNumber(parsed)
    if (fromResponse) return fromResponse
  }

  return String(formData?.groupNumber ?? formData?.policyNumber ?? '').trim()
}

/**
 * Stedi: `payor_id` = `response.payer.payorIdentification`.
 * pVerify fallback: `PverifyPayerCode` / form `payerCode`.
 */
export function resolvePayorIdForEligibilityCheck(
  parsed: Record<string, unknown>,
  formData?: { payerCode?: string }
): string {
  const meta = readEligibilityPersistenceMeta(parsed)
  if (!meta) return ''

  if (meta.integrationProvider === 'stedi') {
    const stediResponse = getStediEligibilityResponse(meta)
    const payer = asRecord(stediResponse?.payer)
    const payorIdentification = String(payer?.payorIdentification ?? '').trim()
    if (payorIdentification) return payorIdentification

    const requestPayload = asRecord(meta.requestPayload.payload)
    const tradingPartner = String(requestPayload?.tradingPartnerServiceId ?? '').trim()
    if (tradingPartner) return tradingPartner
  }

  if (meta.integrationProvider === 'pverify') {
    const response = asRecord(meta.responsePayload)
    const fromResponse = String(
      response?.PverifyPayerCode ?? response?.pverifyPayerCode ?? response?.PayerCode ?? ''
    ).trim()
    if (fromResponse) return fromResponse
  }

  return String(formData?.payerCode ?? '').trim()
}

function resolveBackendPatientId(patientId?: string): string {
  const trimmed = patientId?.trim() ?? ''
  return looksLikeBackendUuid(trimmed) ? trimmed : ''
}

function eligibilityCheckIdentityFields(args: {
  memberId?: string
  patientId?: string
  payorId?: string
  patientIntakeId?: string
  savePatient: boolean
}): Pick<
  CreateEligibilityCheckPayload,
  'member_id' | 'patient_id' | 'payor_id' | 'patient_intake_id' | 'save_patient'
> {
  return {
    ...(args.memberId ? { member_id: args.memberId } : {}),
    ...(args.patientId ? { patient_id: args.patientId } : {}),
    ...(args.payorId ? { payor_id: args.payorId } : {}),
    ...(args.patientIntakeId ? { patient_intake_id: args.patientIntakeId } : {}),
    save_patient: args.savePatient,
  }
}

export type EligibilityCheckPersistResult = {
  eligibilityCheckId?: string
}

export function readEligibilityPersistenceMeta(
  parsed: Record<string, unknown>
): EligibilityPersistenceMeta | null {
  const meta = parsed[ELIGIBILITY_PERSISTENCE_META_KEY]
  if (!meta || typeof meta !== 'object') return null
  const record = meta as Record<string, unknown>
  const integrationProvider = record.integrationProvider
  if (integrationProvider !== 'stedi' && integrationProvider !== 'pverify') return null
  const requestPayload = record.requestPayload
  const responsePayload = record.responsePayload
  if (!requestPayload || typeof requestPayload !== 'object') return null
  if (!responsePayload || typeof responsePayload !== 'object') return null
  return {
    integrationProvider,
    stediApiDate: typeof record.stediApiDate === 'string' ? record.stediApiDate : undefined,
    requestPayload: requestPayload as Record<string, unknown>,
    responsePayload: responsePayload as Record<string, unknown>,
  }
}

/** Removes internal persistence metadata before eligibility view mapping. */
export function stripEligibilityPersistenceMeta(
  parsed: Record<string, unknown>
): Record<string, unknown> {
  const { [ELIGIBILITY_PERSISTENCE_META_KEY]: _removed, ...rest } = parsed
  return rest
}

/** Saves Stedi / pVerify request + response to POST /eligibility-checks only. */
export async function saveEligibilityCheckFromResponse(
  parsed: Record<string, unknown>,
  context: EligibilityCheckPersistContext = {},
  formData?: {
    payerCode?: string
    subscriberID?: string
    policyNumber?: string
    groupNumber?: string
  }
): Promise<EligibilityCheckPersistResult> {
  const meta = readEligibilityPersistenceMeta(parsed)
  if (!meta) {
    throw new Error('Eligibility response is missing persistence metadata.')
  }

  const memberId =
    context.memberId?.trim() ||
    resolveMemberIdForEligibilityCheck(
      {
        subscriberID: formData?.subscriberID,
        policyNumber: formData?.policyNumber,
      },
      parsed
    )
  const patientId = resolveBackendPatientId(context.patientId)
  const payorId = context.payorId?.trim() || resolvePayorIdForEligibilityCheck(parsed, formData)
  const patientIntakeId =
    context.patientIntakeId?.trim() ||
    resolvePatientIntakeIdForEligibilityCheck(parsed, formData)
  const savePatient = context.savePatient ?? true
  const identityFields = eligibilityCheckIdentityFields({
    memberId,
    patientId,
    payorId,
    patientIntakeId,
    savePatient,
  })

  if (meta.integrationProvider === 'stedi') {
    const upstream = asRecord(meta.responsePayload) ?? {}
    const response = asRecord(upstream.response)
    const apiDate = String(upstream.api_date ?? meta.stediApiDate ?? STEDI_API_DATE).trim()
    const upstreamStatusRaw = upstream.upstream_status
    const upstreamStatus =
      typeof upstreamStatusRaw === 'number'
        ? upstreamStatusRaw
        : Number.parseInt(String(upstreamStatusRaw ?? ''), 10)
    const upstreamUrl = String(upstream.upstream_url ?? '').trim()

    if (!response) {
      throw new Error('Stedi save failed: missing response object from eligibility API payload.')
    }
    if (!Number.isFinite(upstreamStatus)) {
      throw new Error('Stedi save failed: missing upstream_status in eligibility API payload.')
    }
    if (!upstreamUrl) {
      throw new Error('Stedi save failed: missing upstream_url in eligibility API payload.')
    }

    const saved = await createEligibilityCheck({
      ...identityFields,
      integration_provider: 'stedi',
      request_payload: meta.requestPayload,
      api_date: apiDate,
      upstream_status: upstreamStatus,
      upstream_url: upstreamUrl,
      response,
    })

    return {
      eligibilityCheckId: typeof saved.id === 'string' ? saved.id : undefined,
    }
  }

  const saved = await createEligibilityCheck({
    ...identityFields,
    integration_provider: meta.integrationProvider,
    status: 'complete',
    request_payload: meta.requestPayload,
    response_payload: meta.responsePayload,
  })

  return {
    eligibilityCheckId: typeof saved.id === 'string' ? saved.id : undefined,
  }
}
